import { Content, EventEditionDetails, IProduct, Nullable } from "../../../types";
import MagentoService from "../../../../services/MagentoService";
import magentoUtils from "../../../utils/magentoUtils";
import { CustomerTypes } from "../../../../components/createBadge/enums/CustomerTypes";
import { ProductContactType, ProductRxType, ProductTypename } from "../../../enums";
import dayjs from "../../../dayjs";
import stringUtils from "../../../utils/stringUtils";
import { BADGE_PRINTED_AT } from "../../../constants";

class MagentoHelper {
  private _groupedProducts: IProduct[] = [];
  private _magentoProducts: IProduct[] = [];

  set magentoProducts(value: IProduct[]) {
    this._magentoProducts = value;
  }

  get magentoProducts(): IProduct[] {
    return this._magentoProducts;
  }

  get groupedProducts(): IProduct[] {
    return this._groupedProducts;
  }

  set groupedProducts(products: IProduct[]) {
    this._groupedProducts = products;
  }

  isGroupedProduct = (product: IProduct, gbsCode: string) => {
    return product.rx_event_edition_code === gbsCode && product.__typename === ProductTypename.GROUPED_PRODUCT;
  };

  hasValidProductData = (product: IProduct) => {
    return product && Object.keys(product).length > 0;
  };

  getGroupedProducts = (products: IProduct[], gbsCode: string) => {
    return products.filter(pr => this.isGroupedProduct(pr, gbsCode) && this.hasValidProductData(pr));
  };

  getGroupedProductsByCustomerType = (customerType: CustomerTypes) => {
    const filteredProducts = this.groupedProducts.filter(
      ({ rx_type, rx_contact_type, __typename }) =>
        rx_type?.toLowerCase() === ProductRxType.REGISTRATION.toLowerCase() &&
        rx_contact_type?.toLowerCase() === ProductContactType.COMPANY.toLowerCase() &&
        __typename === ProductTypename.GROUPED_PRODUCT
    );

    const result: IProduct[] = [];
    filteredProducts.forEach(product => {
      const bundleProduct = this._magentoProducts.find(p => p.sku === product.rx_entitlement_child);
      if (bundleProduct && bundleProduct.rx_customer_type === customerType) {
        result.push(product);
      }
    });

    return result;
  };

  async fetchRegProducts(eventEdition: EventEditionDetails, store: string) {
    const { businessUnitGbsCode, eventGbsCode } = eventEdition;
    const productsResponse = await MagentoService.getCategoryProducts(
      {
        buName: businessUnitGbsCode,
        eventGbsCode,
        categoryName: "reg"
      },
      store,
      eventEdition.id
    );
    const categoryList = productsResponse.data.data.categoryList;
    return categoryList[0].products.items.filter(product => Object.keys(product).length > 0);
  }

  getStoreHeader(eventEditionData: EventEditionDetails, language: string) {
    const { defaultCurrencyCode } = eventEditionData;
    return magentoUtils.getMagentoStore(defaultCurrencyCode, language);
  }

  getProductPrices(product: Nullable<IProduct>) {
    if (!product) {
      return undefined;
    }
    const taxAdjustment = product.price?.regularPrice?.adjustments?.find(p => p.code === "TAX")?.amount.value || 0;
    return {
      withoutTax: product.price_range.minimum_price.regular_price.value - taxAdjustment,
      withTax: product.price_range.minimum_price.regular_price.value
    };
  }

  getProductPrice(product: IProduct) {
    return product.price_range.minimum_price.regular_price.value;
  }

  isProductFree(product: IProduct) {
    return this.getProductPrice(product) === 0;
  }

  isProductPrintAtShow(productCode: string) {
    const products = this.magentoProducts;
    const foundProduct = products.find(product => product.sku === productCode);

    return Boolean(foundProduct?.rx_print_at.includes(BADGE_PRINTED_AT.PRINTED_AT_SHOW));
  }

  isProductPrintAtShowPlus(productCode: string) {
    const products = this.magentoProducts;
    const foundProduct = products.find(product => product.sku === productCode);

    return Boolean(foundProduct?.rx_print_at.includes(BADGE_PRINTED_AT.PRINTED_AT_SHOW_PLUS));
  }

  isProductPrintAtHome(productCode: string) {
    const products = this.magentoProducts;
    const foundProduct = products.find(product => product.sku === productCode);

    return Boolean(foundProduct?.rx_print_at.includes(BADGE_PRINTED_AT.PRINTED_AT_HOME));
  }

  isProductPrintAtShowOrPASPlus(productCode: string) {
    return this.isProductPrintAtShow(productCode) || this.isProductPrintAtShowPlus(productCode);
  }

  getProductDescription(product: IProduct) {
    const { rx_description: description } = product;
    if (!description) {
      return "";
    }
    try {
      const modifiedDescription = `"${stringUtils.replaceAll(description || "{}", '"', '\\"')}"`;
      const parsed = JSON.parse(JSON.parse(modifiedDescription));
      return parsed.description_fields?.map((desc: Content) => desc.content).join("\n");
    } catch (e) {
      console.error("Invalid product description.");
      return "";
    }
  }

  areProductDatesValid(product: IProduct) {
    const { rx_start_date, rx_end_date, rx_available_from, rx_available_to } = product;
    const startDate = magentoUtils.parseMagentoToUTCDate(rx_available_from || rx_start_date);
    const endDate = magentoUtils.parseMagentoToUTCDate(rx_available_to || rx_end_date);

    let isDateValid = !startDate || !endDate || dayjs(new Date()).isBetween(startDate, endDate);
    if (!startDate && endDate) {
      isDateValid = dayjs(new Date()).isBefore(endDate);
    } else if (startDate && !endDate) {
      isDateValid = dayjs(new Date()).isAfter(startDate);
    }
    return isDateValid;
  }
}

export default new MagentoHelper();
